import { apiClient, type PaginatedResponse } from '../../../api/client'
import buildApiUrl, {
  buildPaginationParams,
  type PaginationOptions,
  fetchAllPages,
} from '../../../api/pagination'
import {
  type PaginatedQueryParameters,
  type GenericFilterType,
} from '../../../api/hooks/utils/types'
import { type EntitySearchResponse } from '../../../hooks/useEntitySearch'
import { APIArticleFromJSON } from '../../../generated-types/models/APIArticle'
import { APIBOMFromJSON } from '../../../generated-types/models/APIBOM'
import { ItemsbomApi } from '../../../generated-types/apis/ItemsbomApi'
import { ItemsbomRootsApi } from '../../../generated-types/apis/ItemsbomRootsApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import {
  getPKfromSelf,
  parseNestedObjectList,
  createFileInfo,
  parseNestedObject,
} from '../../../utils/functions'
import { type BOMNode, type BOMEdge, type BOMSearchResult } from '../utils/types'
import { createArticle, getArticleRepresentation } from '../../Item/utils/functions'
import { Article } from '../../Item/utils/types'

const log = new LogTool({ context: 'BOMAPI', enable: true, logLevel: 'warn' })

/**
 * Filter types for BOM queries
 */
export interface BOMFilters extends GenericFilterType {
  name?: string
  article_number?: string
  created_at?: string
  last_modified_at?: string
  organization?: string // Filter by organization name (staff-only)
  request_id?: string // Filter by request ID (staff-only)
}

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }

  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    },
  })
}

/**
 * Create API instances
 */
const bomApi = new ItemsbomApi(getApiConfiguration())
const bomRootsApi = new ItemsbomRootsApi(getApiConfiguration())

/**
 * Create BOMEdge from API response
 */
export function createBOMEdge(bomResponse: any): BOMEdge {
  const apiBOM = APIBOMFromJSON(bomResponse)
  return {
    ...apiBOM,
    parent: parseNestedObject(bomResponse.parent, createArticle),
    child: parseNestedObject(bomResponse.child, createArticle),
  }
}

/**
 * Create BOMNode from Article response
 */
export function createBOMNode(
  articleResponse: any,
  bomBasePk: string,
  bomBaseSelf: string,
  parent?: BOMNode,
  amount?: number
): BOMNode {
  const apiArticle = APIArticleFromJSON(articleResponse)

  // Extract process_type from manufacturing_process object
  let processType = ''
  if (
    articleResponse.manufacturing_process &&
    typeof articleResponse.manufacturing_process === 'object'
  ) {
    processType = articleResponse.manufacturing_process.process_type || ''
  }

  return {
    ...apiArticle,
    key: getPKfromSelf(apiArticle.self),
    articlePk: getPKfromSelf(apiArticle.self),
    bomBasePk,
    bomBaseSelf,
    amount,
    parent,
    children: undefined,
    hasChildren: false,
    leadTime: articleResponse.lead_time,
    files: parseNestedObjectList(articleResponse.files, createFileInfo),
    process: processType,
  }
}

/**
 * Convert BOM form input to API JSON
 */
export function inputToAPIBOMJSON(input: any): any {
  return {
    ...(input.parent && { parent: input.parent }),
    ...(input.child && { child: input.child }),
    ...(input.amount && { amount: parseInt(input.amount) }),
  }
}

/**
 * Fetch paginated BOM bases from API
 */
export async function fetchBOMBases(
  options: PaginatedQueryParameters<any, BOMFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<any>> {
  log.info('Fetching BOM bases with options:', options)

  const url = buildApiUrl(
    'v1/items/bom_bases/',
    options as PaginatedQueryParameters<any, BOMFilters>,
    ids
  )
  const response = await apiClient.get<PaginatedResponse<any>>(url)

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: response.results || [],
  }
}

/**
 * Search BOM bases using Elasticsearch
 * Note: BOM bases are articles with is_bom_base=true, so we use the articles search endpoint
 */
export async function searchBOMBases(
  options: PaginatedQueryParameters<any, BOMFilters> = {}
): Promise<EntitySearchResponse<any>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''

  const searchFilters = {
    ...filters,
    // Always filter for BOM bases only
    is_bom_base: 'true',
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
    // Ensure q is set even if no other filters
    ...(!hasQ && !hasFilters ? { q: '*' } : {}),
  }

  const url = buildApiUrl('v1/search/items/articles/', { filters: searchFilters })
  return apiClient.get<EntitySearchResponse<any>>(url)
}

/**
 * Fetch a BOM base by ID or URL
 */
export async function fetchBOMBase(idOrUrl: string | number, expand?: string[]): Promise<any> {
  log.info('Fetching BOM base:', idOrUrl)

  const url =
    typeof idOrUrl === 'number' || !idOrUrl.includes('/')
      ? `v1/items/bom_bases/${idOrUrl}/`
      : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return response
}

export async function fetchArticleChildren(article: Article): Promise<Article[]> {
  log.info('Begin fetching children for article: ', getArticleRepresentation(article))
  const response = await apiClient.get<any>(article.self + 'children/')
  return response.results.map(createArticle)
}

/**
 * @deprecated Use the fetchArticleChildren function instead
 * Fetch BOM children for a parent article
 */
export async function fetchBOMChildren(
  parentSelf: string,
  bomBaseSelf: string
): Promise<BOMNode[]> {
  log.info('Fetching BOM children for parent:', parentSelf)

  const response = await apiClient.get<any>(`${parentSelf}children/?bom_base=${bomBaseSelf}`)

  const bomNodes = response.results.map((child: any) =>
    createBOMNode(child.child, getPKfromSelf(bomBaseSelf), bomBaseSelf, undefined, child.amount)
  )

  return bomNodes
}

/**
 * Fetch full BOM tree
 */
export async function fetchFullBOM(bomBaseSelf: string, expand?: string[]): Promise<any> {
  log.info('Fetching full BOM:', bomBaseSelf)

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl =
    params.length > 0
      ? `${bomBaseSelf}v1/full_bom/?${params.join('&')}`
      : `${bomBaseSelf}v1/full_bom/`

  const response = await apiClient.get<any>(fetchUrl)
  return response
}

/**
 * Response type for BOM leaf articles endpoint
 */
export interface BOMLeafArticle {
  self: string
  name?: string
  number: string
  files: string
  hasChildren: boolean
  isBomBase?: boolean
  material: string
  bom_amount: number
  parent_path: string[]
  [key: string]: any
}

/**
 * Fetch all leaf articles (articles with no children) from a BOM tree with their amounts.
 *
 * @param articleSelf - The self URL of the BOM root article
 * @param expand - Optional fields to expand in the response
 * @param headers - Optional headers (e.g., for guest requests: { 'X-ACCESS-SECRET': '...' })
 * @returns Promise<BOMLeafArticle[]> - Array of leaf articles with their BOM amounts
 */
export async function fetchBOMLeafArticles(
  articleSelf: string,
  expand?: string[],
  headers?: Record<string, string>
): Promise<BOMLeafArticle[]> {
  log.info('Fetching BOM leaf articles for:', articleSelf)

  // Build the URL - append bom_leaf_articles/ to the article self URL
  const baseUrl = articleSelf.endsWith('/') ? articleSelf : `${articleSelf}/`
  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl =
    params.length > 0
      ? `${baseUrl}bom_leaf_articles/?${params.join('&')}`
      : `${baseUrl}bom_leaf_articles/`

  const response = await apiClient.get<BOMLeafArticle[]>(fetchUrl, headers)
  return response
}

/**
 * Response type for BOM root endpoint
 */
export interface BOMRootArticle {
  self: string
  name?: string
  number: string
  files: string
  hasChildren: boolean
  isBomBase: boolean
  material: string
  [key: string]: any
}

/**
 * Fetch the BOM root article for a given article.
 *
 * The BOM root is the topmost ancestor that has is_bom_base=True.
 * Returns null if the article is not part of any BOM.
 *
 * @param articleSelf - The self URL of the article to find the BOM root for
 * @param headers - Optional headers (e.g., for guest requests: { 'X-ACCESS-SECRET': '...' })
 * @returns Promise<BOMRootArticle | null> - The BOM root article or null if not found
 */
export async function fetchBOMRoot(
  articleSelf: string,
  headers?: Record<string, string>
): Promise<BOMRootArticle | null> {
  log.info('Fetching BOM root for:', articleSelf)

  // Build the URL - append bom_root/ to the article self URL
  const baseUrl = articleSelf.endsWith('/') ? articleSelf : `${articleSelf}/`
  const fetchUrl = `${baseUrl}bom_root/`

  try {
    const response = await apiClient.get<BOMRootArticle>(fetchUrl, headers)
    return response
  } catch (error) {
    // 404 means the article is not part of a BOM - return null
    log.info('Article is not part of any BOM:', articleSelf)
    return null
  }
}

/**
 * Create a new BOM base
 */
export async function createBOMBaseApi(bomData: any): Promise<any> {
  log.info('Creating BOM base:', bomData)

  const response = await apiClient.post<any>('v1/items/bom_bases/', bomData)
  return response
}

/**
 * Create a new BOM edge (add child to parent)
 */
export async function createBOMEdgeApi(bomEdgeData: any): Promise<BOMEdge> {
  log.info('Creating BOM edge:', bomEdgeData)

  const json = inputToAPIBOMJSON(bomEdgeData)
  const response = await bomApi.v1ItemsBomsCreate({
    aPIBOMRequest: json,
  })

  return createBOMEdge(response)
}

/**
 * Update a BOM edge
 */
export async function updateBOMEdgeApi(
  idOrSelf: string | number,
  bomEdgeData: any
): Promise<BOMEdge> {
  log.info('Updating BOM edge:', idOrSelf, bomEdgeData)

  const json = inputToAPIBOMJSON(bomEdgeData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await bomApi.v1ItemsBomsUpdate({
      id: idOrSelf,
      aPIBOMRequest: json,
    })
    return createBOMEdge(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/boms/${idOrSelf}/`
  const response = await apiClient.put<any>(url, json)
  return createBOMEdge(response)
}

/**
 * Patch a BOM edge (partial update)
 */
export async function patchBOMEdgeApi(
  idOrSelf: string | number,
  bomEdgeData: Partial<any>
): Promise<BOMEdge> {
  log.info('Patching BOM edge:', idOrSelf, bomEdgeData)

  const json = inputToAPIBOMJSON(bomEdgeData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await bomApi.v1ItemsBomsPartialUpdate({
      id: idOrSelf,
      aPIPatchedBOMRequest: json,
    })
    return createBOMEdge(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/boms/${idOrSelf}/`
  const response = await apiClient.patch<any>(url, json)
  return createBOMEdge(response)
}

/**
 * Delete a BOM edge
 */
export async function deleteBOMEdgeApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting BOM edge:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await bomApi.v1ItemsBomsDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/boms/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete a BOM base
 */
export async function deleteBOMBaseApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting BOM base:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await bomRootsApi.v1ItemsBomBasesDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/bom_bases/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Fetch all articles that can safely be inserted as a BOM child to the provided node
 * without creating circular BOM relationships
 */
export async function fetchAllowedBomChildren(
  nodeSelf: string,
  options: PaginationOptions = {}
): Promise<any[]> {
  log.info('Fetching allowed BOM children for:', nodeSelf)

  const url = nodeSelf.endsWith('/')
    ? `${nodeSelf}allowed_bom_children/`
    : `${nodeSelf}/allowed_bom_children/`

  // Build parameters
  const params = buildPaginationParams(options)

  // Fetch all pages
  const response = await fetchAllPages<any>(url, params)

  // Convert to APIArticle objects
  return response.results.map((articleResponse: any) => APIArticleFromJSON(articleResponse))
}

/**
 * Recursively fetch all descendants for an article until leaves (articles without children) are reached.
 */
export async function fetchChildrenPage(
  url: string,
  headers?: Record<string, string>
): Promise<PaginatedResponse<any>> {
  log.info('Fetching BOM children page:', url)
  const response = await apiClient.get<PaginatedResponse<any>>(url, headers)
  return response
}

/**
 * Search for articles within a BOM tree structure using Elasticsearch
 * Returns matching articles with all paths from the BOM base to each match
 */
export async function searchBOMTree(
  bomBaseArticleId: string,
  searchQuery: string,
  filters?: {
    name?: string
    number?: string
    description?: string
    created_at?: string
    manufacturing_process?: string
    amount?: string
  }
): Promise<BOMSearchResult[]> {
  log.info('Searching BOM tree for:', searchQuery, 'in BOM base:', bomBaseArticleId)

  // Build query parameters
  const params = new URLSearchParams({
    q: searchQuery,
    bom_base_id: bomBaseArticleId,
  })

  // Add optional filters
  if (filters) {
    if (filters.name) params.append('name', filters.name)
    if (filters.number) params.append('number', filters.number)
    if (filters.description) params.append('description', filters.description)
    if (filters.created_at) params.append('created_at', filters.created_at)
    if (filters.manufacturing_process)
      params.append('manufacturing_process', filters.manufacturing_process)
    if (filters.amount) params.append('amount', filters.amount)
  }

  try {
    const response = await apiClient.get<{
      total: number
      results: BOMSearchResult[]
      message?: string
    }>(`v1/search/items/articles/bom-search/?${params.toString()}`)

    log.info('BOM search completed. Found', response.total, 'matches')
    return response.results
  } catch (error) {
    log.error('Error searching BOM tree:', error)
    throw error
  }
}
